<template>
  <div>
    <template v-if="targetType === NATIONAL">
      <p
        class="g-bold g-heading-s g-text-color-black mt-2 mt-md-4 mb-2 mb-md-4"
      >
        Buy Get Free by National
      </p>
      <v-simple-table dense class="buy-get-free-tier-table">
        <thead>
          <tr>
            <th>TIERS</th>
            <th>BUY QUANTITY</th>
            <th>{{ freeProductColumnLabel }}</th>
            <th>FREE PRODUCT QUANTITY</th>
            <th>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(tier, index) in nationalTiers"
            :key="index"
            class="text-center"
          >
            <td>{{ index + 1 }}</td>
            <td>
              <v-text-field
                v-model="tier.buyQuantity"
                dense
                outlined
                placeholder="Buy Quantity"
                :disabled="!newPromotion"
                :rules="[
                  (v) => !!v || 'This field is required',
                  (v) => v > 0 || 'Must be greater than zero',
                  (v) => /^\d+$/.test(v) || 'No decimals allowed',
                  (v) =>
                    isUniqueBuyQuantity(index, v) ||
                    'Buy quantity cannot be the same as the previous tier',
                  (v) => validateNationalBuyQuantity(index, v)
                ]"
                @input="updateNationalTier(index)"
              ></v-text-field>
            </td>
            <td>
              <v-autocomplete
                v-if="requiresFixedFreeProduct"
                v-model="tier.freeProduct"
                v-model:search-input="freeProductSearchInput"
                class="free-product-autocomplete"
                :items="filteredProductList"
                item-text="text"
                item-title="text"
                item-value="value"
                :disabled="!newPromotion || !hasPromotionProducts"
                dense
                outlined
                placeholder="Select Free Product"
                style="width: 250px"
                @change="handleNationalFreeProductChange(index)"
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ getProductOptionText(item) }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <span
                    class="free-product-selection-text"
                    :title="getProductOptionText(item)"
                  >
                    {{ getProductOptionText(item) }}
                  </span>
                </template>
              </v-autocomplete>
              <div v-else class="text-left py-3">
                Retailer chooses an eligible product
              </div>
            </td>
            <td>
              <v-text-field
                v-model="tier.freeQuantity"
                dense
                outlined
                :disabled="!newPromotion"
                placeholder="Number of Free Products"
                :rules="[
                  (v) => !!v || 'This field is required',
                  (v) => v > 0 || 'Must be greater than zero',
                  (v) => /^\d+$/.test(v) || 'No decimals allowed',
                  (v) =>
                    !exceedsCap(v) ||
                    `Tier exceeds the Max Free Units Cap (${maxFreeUnitsCap}).`
                ]"
                :error="exceedsCap(tier.freeQuantity)"
                :error-messages="
                  exceedsCap(tier.freeQuantity)
                    ? [
                        `Tier exceeds the Max Free Units Cap (${maxFreeUnitsCap}).`
                      ]
                    : []
                "
                @input="updateNationalTier(index)"
              ></v-text-field>
            </td>
            <td>
              <v-btn
                color="secondary"
                icon
                @click="handleNationalPlusClick(index)"
                :disabled="
                  capSingleTier ||
                  !newPromotion ||
                  tier.isAdded ||
                  !isTierFilled(tier) ||
                  exceedsCap(tier.freeQuantity) ||
                  hasAnyCapViolation()
                "
                v-if="!capSingleTier && index === nationalTiers.length - 1"
              >
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>

              <!-- DELETE -->
              <v-btn
                color="error"
                icon
                @click="removeNationalTier(index)"
                v-if="
                  !capSingleTier &&
                  index === nationalTiers.length - 1 &&
                  nationalTiers.length > 1
                "
              >
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </template>

    <template
      v-else-if="
        [
          'STATE',
          'MULTI_STORE_OPERATOR',
          'BANNER_GROUP',
          'RETAILER_STORE'
        ].includes(targetType)
      "
    >
      <p class="g-bold g-heading-s g-text-color-black mt-2 mb-4">
        Buy Get Free By State
      </p>
      <div v-if="syncStateTierValues" class="mb-2 d-flex">
        <v-btn
          outlined
          small
          color="secondary"
          class="add-all-states-btn"
          :disabled="!newPromotion"
          @click="addAllStateOptions"
        >
          <v-icon left small>mdi-plus-circle-multiple-outline</v-icon>
          Add options of all states
        </v-btn>
      </div>
      <div class="bulk-state-update mb-4">
        <v-checkbox
          v-model="syncStateTierValues"
          class="mt-0 pt-0"
          hide-details
          dense
          label="Update all states"
          :disabled="!newPromotion"
        />
        <v-simple-table
          v-if="syncStateTierValues"
          dense
          class="buy-get-free-tier-table mb-2"
        >
          <thead>
            <tr>
              <th>Buy Carton Quantity</th>
              <th>{{ freeProductColumnLabel }}</th>
              <th>Number of Free Carton Products</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr class="text-center">
              <td>
                <v-text-field
                  v-model="allStatesTier.buyQuantity"
                  dense
                  outlined
                  placeholder="Buy Quantity"
                  :disabled="!newPromotion"
                  @input="onAllStatesTierChange"
                />
              </td>
              <td>
                <v-autocomplete
                  v-if="requiresFixedFreeProduct"
                  v-model="allStatesTier.freeProduct"
                  v-model:search-input="freeProductSearchInput"
                  class="free-product-autocomplete"
                  :items="filteredProductList"
                  item-text="text"
                  item-title="text"
                  item-value="value"
                  :disabled="!newPromotion || !hasPromotionProducts"
                  dense
                  outlined
                  placeholder="Select Free Product"
                  style="width: 250px"
                  @change="handleAllStatesFreeProductChange"
                >
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ getProductOptionText(item) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span
                      class="free-product-selection-text"
                      :title="getProductOptionText(item)"
                    >
                      {{ getProductOptionText(item) }}
                    </span>
                  </template>
                </v-autocomplete>
                <div v-else class="text-left py-3">
                  Retailer chooses an eligible product
                </div>
              </td>
              <td>
                <v-text-field
                  v-model="allStatesTier.freeQuantity"
                  dense
                  outlined
                  placeholder="Number of Free Products"
                  :disabled="!newPromotion"
                  @input="onAllStatesTierChange"
                />
              </td>
              <td>
                <v-btn
                  color="secondary"
                  icon
                  :disabled="!newPromotion || !isTierFilled(allStatesTier)"
                  v-tooltip="{
                    content: 'Add tier to all states',
                    visible: $vuetifyDisplay.mdAndDown ? false : undefined
                  }"
                  @click="addTierToAllStates"
                >
                  <v-icon>mdi-plus-circle-multiple-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
      <v-text-field
        v-model="stateFilter"
        dense
        outlined
        clearable
        class="mb-3"
        :label="
          tenantCountryRegion === 'US'
            ? 'Search States'
            : 'Search States / Zones'
        "
        hint="Type state code or name"
        persistent-hint
      />
      <div
        v-for="stateItem in filteredStateTiers"
        :key="stateItem.name"
        class="mb-6"
      >
        <p class="g-bold g-heading-s g-text-color-black">
          {{ stateItem.name }}
        </p>
        <v-simple-table dense class="buy-get-free-tier-table">
          <thead>
            <tr>
              <th>Tier</th>
              <th>Buy Carton Quantity</th>
              <th>{{ freeProductColumnLabel }}</th>
              <th>Number of Free Carton Products</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(tier, tierIndex) in stateItem.tiers"
              :key="tierIndex"
              class="text-center"
            >
              <td>{{ tierIndex + 1 }}</td>
              <td>
                <v-text-field
                  v-model="tier.buyQuantity"
                  dense
                  outlined
                  placeholder="Buy Quantity"
                  :disabled="!newPromotion"
                  :rules="[
                    (v) => !!v || 'This field is required',
                    (v) => v > 0 || 'Must be greater than zero',
                    (v) => /^\d+$/.test(v) || 'No decimals allowed',
                    (v) =>
                      isUniqueStateBuyQuantity(
                        stateItem._index,
                        tierIndex,
                        v
                      ) ||
                      'Buy quantity cannot be the same as the previous tier',
                    (v) => validateBuyQuantity(stateItem._index, tierIndex, v)
                  ]"
                  @input="updateStateTier(stateItem._index, tierIndex)"
                ></v-text-field>
              </td>
              <td>
                <v-autocomplete
                  v-if="requiresFixedFreeProduct"
                  v-model="tier.freeProduct"
                  v-model:search-input="freeProductSearchInput"
                  class="free-product-autocomplete"
                  :items="filteredProductList"
                  item-text="text"
                  item-title="text"
                  item-value="value"
                  :disabled="!newPromotion || !hasPromotionProducts"
                  dense
                  outlined
                  placeholder="Select Free Product"
                  style="width: 250px"
                  @change="
                    handleStateFreeProductChange(stateItem._index, tierIndex)
                  "
                >
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ getProductOptionText(item) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span
                      class="free-product-selection-text"
                      :title="getProductOptionText(item)"
                    >
                      {{ getProductOptionText(item) }}
                    </span>
                  </template>
                </v-autocomplete>
                <div v-else class="text-left py-3">
                  Retailer chooses an eligible product
                </div>
              </td>
              <td>
                <v-text-field
                  v-model="tier.freeQuantity"
                  dense
                  outlined
                  :disabled="!newPromotion"
                  placeholder="Number of Free Products"
                  :rules="[
                    (v) => !!v || 'This field is required',
                    (v) => v > 0 || 'Must be greater than zero',
                    (v) => /^\d+$/.test(v) || 'No decimals allowed',
                    (v) =>
                      !exceedsCap(v) ||
                      `Tier exceeds the Max Free Units Cap (${maxFreeUnitsCap}).`
                  ]"
                  :error="exceedsCap(tier.freeQuantity)"
                  :error-messages="
                    exceedsCap(tier.freeQuantity)
                      ? [
                          `Tier exceeds the Max Free Units Cap (${maxFreeUnitsCap}).`
                        ]
                      : []
                  "
                  @input="updateStateTier(stateItem._index, tierIndex)"
                ></v-text-field>
              </td>
              <td>
                <v-btn
                  color="secondary"
                  icon
                  @click="handleStatePlusClick(stateItem._index, tierIndex)"
                  :disabled="
                    capSingleTier ||
                    !newPromotion ||
                    tier.isAdded ||
                    !isTierFilled(tier) ||
                    exceedsCap(tier.freeQuantity) ||
                    hasAnyCapViolation()
                  "
                  v-if="
                    !capSingleTier && tierIndex === stateItem.tiers.length - 1
                  "
                >
                  <v-icon>mdi-plus-circle</v-icon>
                </v-btn>

                <!-- DELETE -->
                <v-btn
                  icon
                  color="error"
                  @click="removeStateTier(stateItem._index, tierIndex)"
                  v-if="
                    !capSingleTier &&
                    tierIndex === stateItem.tiers.length - 1 &&
                    stateItem.tiers.length > 1
                  "
                  :disabled="!newPromotion"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { BuyGetFreeHelper } from '@/modules/dataMaintenance/helper/BuyGetFreeHelper'
import { formatStateLabel } from '@/utils/stateDisplay'
export default {
  props: {
    targetType: {
      type: String,
      required: true
    },
    selectedProduct: {
      type: Object,
      default: null
    },
    selectedFamilyGroupProducts: {
      type: Array,
      default: () => []
    },
    selectedRangeProducts: {
      type: Array,
      default: () => []
    },
    freeProductCandidates: {
      type: Array,
      default: () => []
    },
    availableStates: {
      type: Array,
      default: () => []
    },
    maxFreeUnitsCap: {
      type: Number,
      default: null
    },
    capSingleTier: { type: Boolean, default: false },
    initialNationalTiers: {
      type: Array,
      default: () => [{ buyQuantity: '', freeProduct: '', freeQuantity: '' }]
    },
    initialStateTiers: {
      type: Array,
      default: () => [
        {
          name: 'ACT',
          tiers: [{ buyQuantity: '', freeProduct: '', freeQuantity: '' }]
        },
        {
          name: 'NSW',
          tiers: [{ buyQuantity: '', freeProduct: '', freeQuantity: '' }]
        },
        {
          name: 'QLD',
          tiers: [{ buyQuantity: '', freeProduct: '', freeQuantity: '' }]
        }
      ]
    },
    newPromotion: {
      type: Boolean,
      required: true
    },
    buyProducts: {
      type: Array,
      default: () => []
    },
    requiresFixedFreeProduct: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      NATIONAL: 'NATIONAL',
      STATE: 'STATE',
      syncStateTierValues: false,
      allStatesTier: {
        buyQuantity: '',
        freeProduct: '',
        freeQuantity: ''
      },
      nationalTiers: this.initialNationalTiers.map((tier) => ({
        ...tier,
        isAdded: false,
        isNew: false
      })),
      stateTiers: [],
      stateFilter: '',
      freeProductSearchInput: ''
    }
  },
  computed: {
    ...mapGetters('tenant', ['tenantCountryRegion']),
    freeProductColumnLabel() {
      return this.requiresFixedFreeProduct
        ? 'FREE PRODUCT'
        : 'ELIGIBLE FREE PRODUCT'
    },
    hasPromotionProducts() {
      return Boolean(
        this.selectedProduct ||
        this.selectedFamilyGroupProducts.length ||
        this.selectedRangeProducts.length ||
        this.buyProducts.length
      )
    },
    filteredProductList() {
      const candidateProducts = this.freeProductCandidates.length
        ? this.freeProductCandidates
        : this.selectedFamilyGroupProducts.length
          ? this.selectedFamilyGroupProducts
          : this.selectedProduct
            ? [this.selectedProduct]
            : this.selectedRangeProducts.length
              ? this.selectedRangeProducts
              : []
      const products = [...candidateProducts]
      const seenProductCodes = new Set()
      this.nationalTiers.forEach((tier) => {
        const freeProductCode = this.normalizeProductCode(tier.freeProduct)
        if (freeProductCode && tier.freeProductName) {
          const exists = products.find(
            (p) => this.normalizeProductCode(p) === freeProductCode
          )
          if (!exists) {
            products.push({
              tradeItemDescription: tier.freeProductName,
              productCode: freeProductCode
            })
          }
        }
      })
      return products
        .map((product) => {
          const value = this.normalizeProductCode(product)
          const text =
            product.tradeItemDescription ||
            product.text ||
            product.title ||
            product.value?.tradeItemDescription

          return {
            text,
            title: text,
            value
          }
        })
        .filter((product) => {
          if (
            !product.text ||
            !product.value ||
            typeof product.text !== 'string' ||
            product.text.trim().startsWith('{')
          ) {
            return false
          }
          if (seenProductCodes.has(product.value)) return false
          seenProductCodes.add(product.value)
          return true
        })
    },
    derivedStateTiers() {
      const productStates = new Set()
      const sourceProducts =
        this.selectedFamilyGroupProducts.length > 0
          ? this.selectedFamilyGroupProducts
          : this.selectedProduct
            ? [this.selectedProduct]
            : this.buyProducts || []

      sourceProducts.forEach((product) => {
        ;(product.rangedStates || []).forEach((state) =>
          productStates.add(state)
        )
      })

      return [...productStates].sort().map((stateCode) => ({
        code: stateCode,
        name: formatStateLabel(stateCode, this.tenantCountryRegion),
        displayName: formatStateLabel(stateCode, this.tenantCountryRegion),
        tiers: [
          {
            buyQuantity: '',
            freeProduct: '',
            freeQuantity: '',
            isNew: false,
            isAdded: false
          }
        ]
      }))
    },
    filteredStateTiers() {
      const term = (this.stateFilter || '').trim().toLowerCase()
      const indexedStates = this.stateTiers.map((state, index) => ({
        ...state,
        _index: index
      }))
      if (!term) return indexedStates
      return indexedStates.filter((state) => {
        const values = [state.name, state.code, state.displayName]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase())
        return values.some((v) => v.includes(term))
      })
    }
  },

  methods: {
    normalizeProductCode(productOrCode) {
      if (typeof productOrCode === 'string') {
        return productOrCode
      }

      if (productOrCode?.productCode) {
        return productOrCode.productCode
      }

      if (typeof productOrCode?.value === 'string') {
        return productOrCode.value
      }

      return productOrCode?.value?.productCode || null
    },

    getProductOptionText(item) {
      return (
        item?.raw?.text ||
        item?.text ||
        item?.title ||
        item?.value?.text ||
        item?.value?.title ||
        ''
      )
    },

    removeNationalTier(index) {
      this.nationalTiers.splice(index, 1)
    },

    updateNationalTier(index) {
      this.nationalTiers[index].isNew = false

      const selectedProduct = this.nationalTiers[index].freeProduct
      if (this.requiresFixedFreeProduct && selectedProduct) {
        this.nationalTiers.forEach((tier) => {
          tier.freeProduct = selectedProduct
        })
      }
      this.$emit('update-national-tiers', this.nationalTiers)
    },

    handleNationalFreeProductChange(index) {
      this.updateNationalTier(index)
      this.finishFreeProductSelection()
    },

    removeStateTier(stateIndex, tierIndex) {
      this.stateTiers[stateIndex].tiers.splice(tierIndex, 1)
    },

    updateStateTier(stateIndex, tierIndex) {
      this.stateTiers[stateIndex].tiers[tierIndex].isNew = false
      const selectedProduct =
        this.stateTiers[stateIndex].tiers[tierIndex].freeProduct
      if (this.requiresFixedFreeProduct && selectedProduct) {
        this.stateTiers.forEach((state) => {
          state.tiers.forEach((tier) => {
            tier.freeProduct = selectedProduct
          })
        })
      }
      this.$emit('update-state-tiers', this.stateTiers)
    },

    handleStateFreeProductChange(stateIndex, tierIndex) {
      this.updateStateTier(stateIndex, tierIndex)
      this.finishFreeProductSelection()
    },

    handleAllStatesFreeProductChange() {
      this.onAllStatesTierChange()
      this.finishFreeProductSelection()
    },

    finishFreeProductSelection() {
      this.freeProductSearchInput = ''
      this.$nextTick(() => {
        if (typeof document === 'undefined') return
        const activeElement = document.activeElement
        if (activeElement instanceof HTMLElement) {
          activeElement.blur()
        }
      })
    },

    getProductName(productCode) {
      const product =
        this.selectedFamilyGroupProducts.find(
          (p) => p.productCode === productCode
        ) || this.selectedProduct
      return product?.tradeItemDescription
    },

    isTierFilled(tier) {
      return (
        tier.buyQuantity &&
        (!this.requiresFixedFreeProduct || tier.freeProduct) &&
        tier.freeQuantity &&
        tier.buyQuantity > 0 &&
        tier.freeQuantity > 0 &&
        !this.exceedsCap(tier.freeQuantity)
      )
    },

    isUniqueBuyQuantity(index, value) {
      if (index === 0) return true
      return value !== this.nationalTiers[index - 1].buyQuantity
    },

    isUniqueStateBuyQuantity(stateIndex, tierIndex, value) {
      if (tierIndex === 0) return true
      return (
        value !== this.stateTiers[stateIndex].tiers[tierIndex - 1].buyQuantity
      )
    },

    handleNationalPlusClick(index) {
      const lastTier = this.nationalTiers[index]
      if (
        !BuyGetFreeHelper.isTierValid(lastTier, this.requiresFixedFreeProduct)
      ) {
        return
      }

      if (!BuyGetFreeHelper.isTierDuplicate(this.nationalTiers, lastTier)) {
        return
      }
      const defaultProduct = this.requiresFixedFreeProduct
        ? this.nationalTiers[0]?.freeProduct || ''
        : ''
      lastTier.isNew = false
      lastTier.isAdded = true
      if (this.nationalTiers.length < 3) {
        this.nationalTiers.push({
          buyQuantity: '',
          freeProduct: defaultProduct,
          freeQuantity: '',
          isNew: true,
          isAdded: false
        })
      }
      this.$emit('update-national-tiers', this.nationalTiers)
    },

    handleStatePlusClick(stateIndex, tierIndex) {
      const lastTier = this.stateTiers[stateIndex].tiers[tierIndex]

      if (
        !BuyGetFreeHelper.isTierValid(lastTier, this.requiresFixedFreeProduct)
      ) {
        this.$emit('show-error', 'Please fill all fields before confirming.')
        return
      }

      if (
        !BuyGetFreeHelper.isTierDuplicate(
          this.stateTiers[stateIndex].tiers,
          lastTier
        )
      ) {
        this.$emit('show-error', 'Duplicate Buy Quantity is not allowed.')
        return
      }

      const defaultProduct = this.requiresFixedFreeProduct
        ? this.stateTiers[stateIndex].tiers[0]?.freeProduct || ''
        : ''

      lastTier.isNew = false
      lastTier.isAdded = true

      if (this.stateTiers[stateIndex].tiers.length < 3) {
        this.stateTiers[stateIndex].tiers.push({
          buyQuantity: '',
          freeProduct: defaultProduct,
          freeQuantity: '',
          isNew: true,
          isAdded: false
        })
      }

      this.$emit('update-state-tiers', this.stateTiers)
    },

    validateBuyQuantity(stateIndex, tierIndex, value) {
      if (tierIndex === 0) return true

      const currentBuyQty = parseInt(value || 0, 10)
      const previousBuyQty = parseInt(
        this.stateTiers[stateIndex].tiers[tierIndex - 1].buyQuantity || 0,
        10
      )

      return (
        currentBuyQty > previousBuyQty ||
        'Buy quantity must be higher than the previous tier'
      )
    },

    validateNationalBuyQuantity(index, value) {
      if (index === 0) return true

      const currentBuyQty = parseInt(value || 0, 10)
      const previousBuyQty = parseInt(
        this.nationalTiers[index - 1].buyQuantity || 0,
        10
      )

      return (
        currentBuyQty > previousBuyQty ||
        'Buy quantity must be higher than the previous tier'
      )
    },
    populateDynamicStateTiers() {
      this.stateTiers = this.derivedStateTiers
    },

    addAllStateOptions() {
      const fallbackStates = this.derivedStateTiers.map(
        (stateTier) => stateTier.code || stateTier.name
      )
      const statesToAdd =
        this.availableStates.length > 0 ? this.availableStates : fallbackStates

      const existingStates = new Set(
        this.stateTiers.map((stateTier) => stateTier.code || stateTier.name)
      )
      const nextStateTiers = [...this.stateTiers]

      statesToAdd.forEach((stateCode) => {
        if (!existingStates.has(stateCode)) {
          nextStateTiers.push({
            code: stateCode,
            name: formatStateLabel(stateCode, this.tenantCountryRegion),
            displayName: formatStateLabel(stateCode, this.tenantCountryRegion),
            tiers: [{ buyQuantity: '', freeProduct: '', freeQuantity: '' }]
          })
        }
      })

      this.stateTiers = nextStateTiers.sort((a, b) =>
        a.name.localeCompare(b.name)
      )
      this.$emit('update-state-tiers', this.stateTiers)
    },
    onAllStatesTierChange() {
      if (!this.syncStateTierValues) return
      this.applyAllStatesTierToCurrentTier()
    },
    applyAllStatesTierToCurrentTier() {
      // Each "Add tier to all states" click appends a new tier to every
      // state's array, so the tier the bulk row should currently be editing
      // is always the last one - not hardcoded to the first, or edits to a
      // 2nd/3rd tier via this shared row would silently keep overwriting
      // tier 1 instead.
      const nextStateTiers = this.stateTiers.map((state) => {
        const tiers = state.tiers?.length
          ? [...state.tiers]
          : [{ buyQuantity: '', freeProduct: '', freeQuantity: '' }]
        const currentIndex = tiers.length - 1
        const currentTier = {
          ...tiers[currentIndex],
          buyQuantity: this.allStatesTier.buyQuantity,
          freeProduct: this.requiresFixedFreeProduct
            ? this.allStatesTier.freeProduct
            : '',
          freeQuantity: this.allStatesTier.freeQuantity,
          isNew: false
        }
        tiers[currentIndex] = currentTier
        return { ...state, tiers }
      })
      this.stateTiers = nextStateTiers
      this.$emit('update-state-tiers', this.stateTiers)
    },
    addTierToAllStates() {
      if (
        !BuyGetFreeHelper.isTierValid(
          this.allStatesTier,
          this.requiresFixedFreeProduct
        )
      ) {
        this.$emit('show-error', 'Please fill all fields before confirming.')
        return
      }

      const nextStateTiers = this.stateTiers.map((state) => {
        const tiers = state.tiers?.length
          ? [...state.tiers]
          : [{ buyQuantity: '', freeProduct: '', freeQuantity: '' }]
        const lastTier = { ...tiers[tiers.length - 1] }

        if (
          !BuyGetFreeHelper.isTierValid(lastTier, this.requiresFixedFreeProduct)
        ) {
          return { ...state, tiers }
        }

        if (!BuyGetFreeHelper.isTierDuplicate(tiers, lastTier)) {
          return { ...state, tiers }
        }

        if (tiers.length >= 3) {
          return { ...state, tiers }
        }

        lastTier.isNew = false
        lastTier.isAdded = true
        tiers[tiers.length - 1] = lastTier
        tiers.push({
          buyQuantity: '',
          freeProduct: this.requiresFixedFreeProduct
            ? this.allStatesTier.freeProduct || lastTier.freeProduct
            : '',
          freeQuantity: '',
          isNew: true,
          isAdded: false
        })

        return { ...state, tiers }
      })

      this.stateTiers = nextStateTiers
      this.$emit('update-state-tiers', this.stateTiers)
    },

    exceedsCap(v) {
      if (!this.maxFreeUnitsCap) return false
      const n = Number(v)
      if (!Number.isFinite(n)) return false
      return n > this.maxFreeUnitsCap
    },

    hasAnyCapViolation() {
      if (!this.maxFreeUnitsCap) return false
      if (this.targetType === this.NATIONAL) {
        return (this.nationalTiers || []).some((t) =>
          this.exceedsCap(t.freeQuantity)
        )
      }
      return (this.stateTiers || []).some((s) =>
        (s.tiers || []).some((t) => this.exceedsCap(t.freeQuantity))
      )
    },

    stateHasCapViolation(stateIndex) {
      if (!this.maxFreeUnitsCap) return false
      const state = this.stateTiers[stateIndex]
      return (state?.tiers || []).some((t) => this.exceedsCap(t.freeQuantity))
    },

    prefillSelectedProductAsFreeProduct() {
      if (
        !this.newPromotion ||
        !this.requiresFixedFreeProduct ||
        !this.selectedProduct?.productCode
      ) {
        return
      }

      const freeProduct = this.selectedProduct.productCode
      this.nationalTiers = this.nationalTiers.map((tier) => ({
        ...tier,
        freeProduct
      }))
      this.stateTiers = this.stateTiers.map((state) => ({
        ...state,
        tiers: state.tiers.map((tier) => ({
          ...tier,
          freeProduct
        }))
      }))
      this.allStatesTier = {
        ...this.allStatesTier,
        freeProduct
      }

      this.$emit('update-national-tiers', this.nationalTiers)
      this.$emit('update-state-tiers', this.stateTiers)
    }
  },
  watch: {
    selectedProduct() {
      this.populateDynamicStateTiers()
      this.prefillSelectedProductAsFreeProduct()
    },
    selectedFamilyGroupProducts: 'populateDynamicStateTiers',
    buyProducts: 'populateDynamicStateTiers',
    requiresFixedFreeProduct(isRequired) {
      if (isRequired) return
      this.allStatesTier = {
        ...this.allStatesTier,
        freeProduct: ''
      }
      this.nationalTiers = this.nationalTiers.map((tier) => ({
        ...tier,
        freeProduct: ''
      }))
      this.stateTiers = this.stateTiers.map((state) => ({
        ...state,
        tiers: state.tiers.map((tier) => ({
          ...tier,
          freeProduct: ''
        }))
      }))
      this.$emit('update-national-tiers', this.nationalTiers)
      this.$emit('update-state-tiers', this.stateTiers)
    },
    syncStateTierValues(isEnabled) {
      if (isEnabled) {
        this.applyAllStatesTierToCurrentTier()
      }
    }
  },

  mounted() {
    if (this.newPromotion) {
      this.populateDynamicStateTiers()
      this.prefillSelectedProductAsFreeProduct()
    } else {
      this.stateTiers = this.initialStateTiers.map((state) => ({
        ...state,
        tiers: state.tiers.map((tier) => ({ ...tier, isNew: false }))
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.buy-get-free-tier-table {
  th {
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    padding: 10px;
  }
  td {
    text-align: center;
    vertical-align: middle;
    padding: 10px;
  }
  .text-center {
    text-align: center;
  }

  .free-product-autocomplete {
    :deep(.v-field__input),
    :deep(.v-select__selection) {
      min-width: 0;
    }

    :deep(.v-field__input) {
      flex-wrap: nowrap;
      overflow: hidden;
    }
  }

  .free-product-selection-text {
    display: block;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
